import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExtensionIcon from '@mui/icons-material/Extension';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LanIcon from '@mui/icons-material/Lan';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useNavigate } from 'react-router-dom';
import {
  addDoc,
  collection,
  doc,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../firebase';
import { useFirestoreQuery } from '../../hooks/useFirestoreQuery';
import { useCompanyUsers } from '../../hooks/useCompanyUsers';
import { usePermissions } from '../../hooks/usePermissions';
import { useWorkspace } from '../../context/WorkspaceContext';
import {
  ACTIVE_MODULE_DESCRIPTIONS,
  ACTIVE_MODULE_LABELS,
  BASE_PLATFORM_MODULE_KEYS,
  normalizeActiveModules,
  normalizeCustomAiModules,
} from '../../lib/companyActiveModules';

const APP_TYPES = ['Web', 'Mobile', 'Internal'];
const STATUS_OPTIONS = ['Active', 'Planned', 'Deprecated', 'Suspended'];

/** Core platform features that can be enabled per app/site. */
const APP_MODULES = [
  { key: 'ticketing', label: 'Ticketing', description: 'Support tickets & queues' },
  { key: 'chat', label: 'Chat', description: 'Team & customer messaging' },
  { key: 'crm', label: 'CRM', description: 'Outbound email campaigns' },
  { key: 'sprints', label: 'Sprints', description: 'Agile planning & boards' },
  {
    key: 'uptimeMonitoring',
    label: 'Uptime Monitoring',
    description: 'Liveness checks & alerts',
  },
];

const DEFAULT_APP_MODULES = {
  ticketing: true,
  chat: false,
  crm: false,
  sprints: false,
  uptimeMonitoring: false,
};

/**
 * Coerce a stored modules map into the full known-key shape (older app docs
 * may lack the field or newer keys).
 * @param {Record<string, boolean>} [raw]
 * @returns {Record<string, boolean>}
 */
function normalizeModules(raw) {
  const out = {};
  APP_MODULES.forEach((mod) => {
    out[mod.key] = Boolean(raw?.[mod.key]);
  });
  return out;
}

const TYPE_ICONS = {
  Web: LanguageIcon,
  Mobile: PhoneIphoneIcon,
  Internal: LanIcon,
};

function displayUserLabel(user) {
  return (
    user?.displayName ||
    user?.fullName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    user?.uid ||
    '?'
  );
}

function formatInfraCost(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return '$0/mo';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo`;
}

function statusColor(status) {
  if (status === 'Active') return 'success';
  if (status === 'Planned') return 'info';
  if (status === 'Suspended') return 'warning';
  if (status === 'Deprecated') return 'default';
  return 'default';
}

function ProductCard({ app, usersByUid, onConfigure, onEdit }) {
  const TypeIcon = TYPE_ICONS[app.type] || LanguageIcon;
  const assigned = (Array.isArray(app.assignedTeamUids) ? app.assignedTeamUids : [])
    .map((uid) => usersByUid[uid])
    .filter(Boolean);

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
              <TypeIcon fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                {app.appName || 'Untitled'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {app.type || 'Web'}
              </Typography>
            </Box>
          </Stack>
          <Chip size="small" label={app.status || 'Active'} color={statusColor(app.status)} />
        </Stack>

        <Box
          sx={{
            py: 1.5,
            px: 2,
            borderRadius: 1,
            bgcolor: 'action.hover',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Monthly infra cost
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {formatInfraCost(app.infraCostMonthly)}
          </Typography>
        </Box>

        <Box sx={{ mt: 'auto' }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
            Assigned team
          </Typography>
          {assigned.length > 0 ? (
            <AvatarGroup max={5} sx={{ justifyContent: 'flex-start' }}>
              {assigned.map((member) => (
                <Tooltip key={member.uid} title={displayUserLabel(member)}>
                  <Avatar
                    src={member.photoURL || member.avatarUrl || undefined}
                    alt={displayUserLabel(member)}
                    sx={{ width: 32, height: 32, fontSize: 14 }}
                  >
                    {displayUserLabel(member).charAt(0).toUpperCase()}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              No team assigned
            </Typography>
          )}
        </Box>

        {(onEdit || onConfigure) && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            {onEdit && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlinedIcon fontSize="small" />}
                onClick={() => onEdit(app)}
              >
                Edit
              </Button>
            )}
            {onConfigure && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SettingsOutlinedIcon fontSize="small" />}
                onClick={() => onConfigure(app)}
              >
                Configure CRM
              </Button>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Company Admin portfolio of apps/sites (Firestore `apps` collection).
 * @param {{ userData?: { companyId?: string, role?: string } }} props
 */
export default function AppsAndSites({ userData }) {
  const companyId = userData?.companyId;
  const navigate = useNavigate();
  const isCompanyAdmin = userData?.role === 'company_admin';
  const { checkPermission } = usePermissions(userData);
  const canWritePortfolio = checkPermission('portfolio', 'write');
  const { workspace } = useWorkspace();

  const handleConfigureApp = (app) => {
    navigate('/dashboard/crm', {
      state: { activeTab: 'CrmMarketing', crmAppId: app.id },
    });
  };

  const basePlatformModules = useMemo(() => {
    const active = normalizeActiveModules(workspace?.activeModules);
    return BASE_PLATFORM_MODULE_KEYS.map((key) => ({
      key,
      label: ACTIVE_MODULE_LABELS[key] || key,
      description: ACTIVE_MODULE_DESCRIPTIONS[key] || '',
      enabled: active[key] === true,
    }));
  }, [workspace?.activeModules]);

  const customAiModules = useMemo(
    () => normalizeCustomAiModules(workspace?.customAiModules),
    [workspace?.customAiModules],
  );

  const appsQuery = useMemo(() => {
    if (!companyId) return null;
    return query(collection(db, 'apps'), where('companyId', '==', companyId));
  }, [companyId]);

  const { data: appsRaw, loading, error: queryError } = useFirestoreQuery(appsQuery, {
    enabled: Boolean(companyId),
  });

  const { users: companyUsers, loading: usersLoading } = useCompanyUsers(companyId, {
    enabled: Boolean(companyId),
  });

  const apps = useMemo(() => {
    const rows = [...appsRaw];
    rows.sort((a, b) => (a.appName || '').localeCompare(b.appName || ''));
    return rows;
  }, [appsRaw]);

  const usersByUid = useMemo(() => {
    const map = {};
    companyUsers.forEach((u) => {
      map[u.uid] = u;
    });
    return map;
  }, [companyUsers]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [appName, setAppName] = useState('');
  const [type, setType] = useState('Web');
  const [infraCostMonthly, setInfraCostMonthly] = useState('');
  const [status, setStatus] = useState('Active');
  const [assignedTeam, setAssignedTeam] = useState([]);
  const [modules, setModules] = useState(DEFAULT_APP_MODULES);

  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [analysisText, setAnalysisText] = useState('');
  const [analysisTotalCost, setAnalysisTotalCost] = useState(null);

  const resetForm = () => {
    setAppName('');
    setType('Web');
    setInfraCostMonthly('');
    setStatus('Active');
    setAssignedTeam([]);
    setModules(DEFAULT_APP_MODULES);
    setFormError('');
  };

  const toggleModule = (key) => {
    setModules((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const openCreate = () => {
    resetForm();
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (app) => {
    setEditingId(app.id);
    setAppName(app.appName || '');
    setType(APP_TYPES.includes(app.type) ? app.type : 'Web');
    setInfraCostMonthly(
      app.infraCostMonthly != null ? String(app.infraCostMonthly) : '',
    );
    setStatus(STATUS_OPTIONS.includes(app.status) ? app.status : 'Active');
    setAssignedTeam(
      (Array.isArray(app.assignedTeamUids) ? app.assignedTeamUids : [])
        .map((uid) => usersByUid[uid])
        .filter(Boolean),
    );
    setModules(normalizeModules(app.modules));
    setFormError('');
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    const name = appName.trim();
    if (!name) {
      setFormError('App name is required.');
      return;
    }
    if (!companyId) return;

    const cost = Number(infraCostMonthly);
    if (infraCostMonthly !== '' && (!Number.isFinite(cost) || cost < 0)) {
      setFormError('Infra cost must be a non-negative number.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      if (editingId) {
        await updateDoc(doc(db, 'apps', editingId), {
          appName: name,
          type,
          infraCostMonthly: Number.isFinite(cost) ? cost : 0,
          status,
          assignedTeamUids: assignedTeam.map((u) => u.uid),
          modules,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || null,
        });
      } else {
        await addDoc(collection(db, 'apps'), {
          companyId,
          appName: name,
          type,
          infraCostMonthly: Number.isFinite(cost) ? cost : 0,
          status,
          assignedTeamUids: assignedTeam.map((u) => u.uid),
          modules,
          createdAt: serverTimestamp(),
          createdBy: auth.currentUser?.uid || null,
        });
      }
      setDialogOpen(false);
      setEditingId(null);
      resetForm();
    } catch (e) {
      console.error('AppsAndSites save:', e);
      setFormError(e.message || 'Could not save app.');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAnalysis = async () => {
    if (!companyId) return;
    setAnalysisLoading(true);
    setAnalysisError('');
    try {
      const generatePortfolioAnalysis = httpsCallable(functions, 'generatePortfolioAnalysis');
      const { data } = await generatePortfolioAnalysis({ companyId });
      setAnalysisText(data?.analysis || '');
      setAnalysisTotalCost(
        typeof data?.totalMonthlyInfraCost === 'number' ? data.totalMonthlyInfraCost : null,
      );
    } catch (e) {
      console.error('generatePortfolioAnalysis:', e);
      setAnalysisError(e.message || 'Could not generate portfolio analysis.');
      setAnalysisText('');
      setAnalysisTotalCost(null);
    } finally {
      setAnalysisLoading(false);
    }
  };

  if (!companyId) {
    return (
      <Alert severity="info" sx={{ maxWidth: 560 }}>
        Your account is not linked to a company yet.
      </Alert>
    );
  }

  const pageLoading = loading || usersLoading;
  const loadError = queryError?.message || '';

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Product Portfolio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Platform modules, custom AI modules, and infra spend across your web, mobile, and internal apps.
          </Typography>
        </Box>
        {canWritePortfolio && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add new app/site
          </Button>
        )}
      </Stack>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Base Platform Modules
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Core ShiftyPM products enabled for your tenant (managed by Super Admin rollouts and workspace requests).
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {basePlatformModules.map((mod) => (
          <Grid key={mod.key} size={{ xs: 12, sm: 6, lg: 4 }}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                bgcolor: mod.enabled ? 'action.selected' : 'background.paper',
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <ExtensionIcon color={mod.enabled ? 'primary' : 'disabled'} fontSize="small" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                      {mod.label}
                    </Typography>
                  </Stack>
                  <Chip
                    size="small"
                    label={mod.enabled ? 'Enabled' : 'Off'}
                    color={mod.enabled ? 'success' : 'default'}
                    variant={mod.enabled ? 'filled' : 'outlined'}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {mod.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Custom AI Modules
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Modules built specifically for your company by AI workers (`customAiModules` on your company profile).
      </Typography>
      {customAiModules.length === 0 ? (
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent sx={{ py: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <SmartToyOutlinedIcon color="disabled" />
              <Typography variant="body2" color="text.secondary">
                No custom AI modules yet. When agents publish tenant-specific tools, they appear here.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {customAiModules.map((mod) => (
            <Grid key={mod.id} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <SmartToyOutlinedIcon color="secondary" fontSize="small" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                        {mod.name}
                      </Typography>
                    </Stack>
                    <Chip
                      size="small"
                      label={mod.status || 'active'}
                      color={mod.status === 'active' ? 'secondary' : 'default'}
                      variant="outlined"
                    />
                  </Stack>
                  {mod.description ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {mod.description}
                    </Typography>
                  ) : null}
                  {(mod.agentName || mod.agentId) && (
                    <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
                      Built by {mod.agentName || mod.agentId}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Apps & sites
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Infra spend and team ownership across your web, mobile, and internal apps.
      </Typography>

      <Card
        variant="outlined"
        sx={{
          mb: 3,
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(103,58,183,0.15) 0%, rgba(25,118,210,0.12) 100%)'
              : 'linear-gradient(135deg, rgba(103,58,183,0.08) 0%, rgba(25,118,210,0.06) 100%)',
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1 }}>
                <AutoAwesomeIcon color="primary" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    AI Portfolio Analysis
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Calculate total infra costs and summarize active app statuses with Gemini.
                  </Typography>
                </Box>
              </Stack>
              {canWritePortfolio && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={analysisLoading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                  onClick={handleGenerateAnalysis}
                  disabled={analysisLoading || apps.length === 0}
                  sx={{ flexShrink: 0 }}
                >
                  {analysisLoading ? 'Analyzing…' : 'Generate AI Analysis'}
                </Button>
              )}
            </Stack>

            {analysisError && (
              <Alert severity="error" onClose={() => setAnalysisError('')}>
                {analysisError}
              </Alert>
            )}

            {analysisTotalCost != null && (
              <Box
                sx={{
                  py: 1.25,
                  px: 2,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Total monthly infrastructure (portfolio)
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {formatInfraCost(analysisTotalCost)}
                </Typography>
              </Box>
            )}

            {analysisText ? (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {analysisText}
              </Typography>
            ) : (
              !analysisLoading &&
              !analysisError && (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  {apps.length === 0
                    ? 'Add at least one app to run portfolio analysis.'
                    : 'Click Generate AI Analysis for an executive summary from your fractional CFO & CTO.'}
                </Typography>
              )
            )}
          </Stack>
        </CardContent>
      </Card>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {pageLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : apps.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No apps or sites in your portfolio yet.
            </Typography>
            {canWritePortfolio && (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
                Add your first app
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {apps.map((app) => (
            <Grid key={app.id} size={{ xs: 12, sm: 6, lg: 4 }}>
              <ProductCard
                app={app}
                usersByUid={usersByUid}
                onEdit={canWritePortfolio ? openEdit : undefined}
                onConfigure={isCompanyAdmin ? handleConfigureApp : undefined}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={saving ? undefined : () => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit app/site' : 'Add new app/site'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 0.5 }}>
            {formError && <Alert severity="error">{formError}</Alert>}

            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                App details
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="App name"
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  fullWidth
                  required
                  autoFocus
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel id="app-type-label">Type</InputLabel>
                    <Select
                      labelId="app-type-label"
                      label="Type"
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                    >
                      {APP_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="app-status-label">Status</InputLabel>
                    <Select
                      labelId="app-status-label"
                      label="Status"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <TextField
                  label="Monthly infra cost"
                  type="number"
                  value={infraCostMonthly}
                  onChange={(e) => setInfraCostMonthly(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  helperText="Hosting, cloud, licenses, and other recurring infrastructure"
                />
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                Enabled modules
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Choose which platform features are provisioned for this app.
              </Typography>
              <Grid container spacing={1}>
                {APP_MODULES.map((mod) => (
                  <Grid key={mod.key} size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      sx={{
                        m: 0,
                        width: '100%',
                        alignItems: 'flex-start',
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                        p: 1,
                        bgcolor: modules[mod.key] ? 'action.selected' : 'transparent',
                      }}
                      control={
                        <Switch
                          checked={Boolean(modules[mod.key])}
                          onChange={() => toggleModule(mod.key)}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ ml: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {mod.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {mod.description}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                Team assignment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Assign members to this app. People can be assigned to multiple apps.
              </Typography>
              <Autocomplete
                multiple
                options={companyUsers}
                value={assignedTeam}
                onChange={(_, value) => setAssignedTeam(value)}
                getOptionLabel={(option) => displayUserLabel(option)}
                isOptionEqualToValue={(a, b) => a.uid === b.uid}
                loading={usersLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned team"
                    placeholder="Select members"
                    helperText={
                      usersLoading
                        ? 'Loading workspace members…'
                        : `${companyUsers.length} member${companyUsers.length === 1 ? '' : 's'} in your workspace.`
                    }
                  />
                )}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving
              ? editingId
                ? 'Saving…'
                : 'Creating…'
              : editingId
                ? 'Save changes'
                : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
